import { useEffect, useRef, useState, MouseEvent as ReactMouseEvent } from "react";
import { createRoot } from "react-dom/client";
import { styled } from "styled-components";
import { buttonReset, colors, font } from "./lib/sharedStyles";
import SearchFlyout from "./components/SearchFlyout";
import { SearchContextProvider, useSearchContext } from "./contexts/SearchContext";
import { X } from "@fenderdigital/ui/Icons";

const root = document.getElementById('react-searchspring-search') as HTMLElement;

const Container = styled.div<{ $hasFocus: boolean; }>`
  position: absolute;
  z-index: 2;
  left: 0;
  // Todo: where is this gap coming from?
  top: calc(100% - 2px);
  width: 100%;
  height: 56px;
  padding: 8px 16px 16px;
  background-color: ${colors.fender.white};

  ${({ $hasFocus }) => $hasFocus && `
    padding: 24px 16px 0;
  `}

  // Dawn breakpoint
  @media (min-width: 1150px) {
    top: 16px;
    right: 0;
    width: 600px;
    height: 40px;
    margin: 0 auto;
    padding: 0;
  }
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;

  > form {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  box-shadow: none;
  padding: 0 0 0 50px;
  border-radius: 100px;
  ${font('Instrument Sans', 400, '12px', '18px')};
  letter-spacing: 0.12px;
  color: ${colors.fender.grey05};
  background-color: ${colors.fender.grey01};

  @media (min-width: 1150px) {
    padding-left: 64px;
  }
`;

const SearchButton = styled.button`
  ${buttonReset()}
  position: absolute;
  top: 7px;
  left: 16px;
  cursor: pointer;

  @media (min-width: 1150px) {
    top: 11px;
    left: 24px;
  }
`;

const CancelButton = styled.button`
  ${buttonReset()};
  ${font('Instrument Sans', 600, '12px', '18px')};
  letter-spacing: 0.12px;
  color: ${colors.fender.grey05};
  margin-left: 24px;
  cursor: pointer;

  @media (min-width: 1150px) {
    text-decoration: underline;
  }
`;

const ClearInputButton = styled.button`
  ${buttonReset()};
  position: absolute;
  top: 4px;
  right: 8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 32px;
  cursor: pointer;

  &:hover {
    background-color: ${colors.fender.grey02};
  }

  @media (min-width: 1150px) {
    width: 32px;
    height: 32px
  }
`;

const SearchspringSearch = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const searchComponentsRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [header, setHeader] = useState<HTMLElement>();
  const { searchInputValue, updateSearchInput } = useSearchContext();
  const searchUrl = root.dataset.searchUrl;
  const [transformHeight, setTransformHeight] = useState(70);
  const [pencilBannerHeight, setPencilBannerHeight] = useState(0);
  const [isTransformed, setIsTransformed] = useState(false);

  const handleScroll = () => {
    if (window.innerWidth >= 1150) {
      setHasFocus(false);
      inputRef?.current?.blur();
    }
  }

  const handleIconClick = () => {
    setHasFocus(true);
    inputRef?.current?.focus();

    if (window.innerWidth > 1150 && searchInputValue && formRef && formRef.current) {
      formRef.current.submit();
    }
  }

  const handleClick = (e: MouseEvent) => {
    if (searchComponentsRef?.current) {
      // @ts-ignore
      if (!searchComponentsRef.current.contains(e.target)) {
        setHasFocus(false);
      }
    }
  }

  const handleClearClick = (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateSearchInput('');
  }

  const getTransformHeight = () => {
    const stickyHeaderEl: HTMLElement|null = document.querySelector('.header-wrapper');
    let newHeight = stickyHeaderEl?.offsetHeight || 0;
    if (newHeight > 0) {
      setTransformHeight(newHeight);
    }

    const pencilBannerEl: HTMLElement|null = document.querySelector('.utility-bar');
    let bannerHeight = pencilBannerEl?.offsetHeight || 0;
    if (bannerHeight > 0) {
      setPencilBannerHeight(bannerHeight);
    }
  }

  const resetHeaderTransform = () => {
    if (!header) return;
    document.documentElement.style.overflow = 'initial';
    header.style.transform = '';
    setIsTransformed(false);
  }

  const transformHeader = () => {
    if (!header) return;

    if (window.innerWidth >= 1150) {
      // Reset the header transform if resizing from mobile to desktop 
      // with the flyout open else the header will be stuck out of view.
      if (isTransformed) {
        resetHeaderTransform();
      }
    } else {
      // Transform the header on mobile when opening the search flyout,
      // and reset it when it is closed or loses focus.
      if (hasFocus && !isTransformed) {
        let translateVal = transformHeight;
        if (window.scrollY == 0 && pencilBannerHeight > 0) {
          translateVal += pencilBannerHeight;
        }

        document.documentElement.style.overflow = 'hidden';
        header.style.transform = `translateY(-${translateVal}px)`
        setIsTransformed(true);
      } else if (!hasFocus && isTransformed) {
        resetHeaderTransform();
      }
    }
  }

  const handleResize = () => {
    getTransformHeight();
    transformHeader();
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => { window.removeEventListener('scroll', handleScroll); }
  });

  useEffect(() => {
    window.addEventListener('click', handleClick);
    return () => { window.removeEventListener('click', handleClick); }
  });

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => { window.removeEventListener('resize', handleResize); }
  });

  useEffect(() => {
    const headerEl: HTMLElement|null = document.querySelector('.header');
    if (headerEl) {
      headerEl.style.transition = 'transform 250ms ease';
      setHeader(headerEl);
    }
    
    getTransformHeight();
  }, []);

  useEffect(() => {
    transformHeader();
  }, [hasFocus]);

  return (
    <div ref={searchComponentsRef}>
      <Container $hasFocus={hasFocus} className="searchspring-search-container">
        <InputWrap>
          <form action={searchUrl} ref={formRef}>
            <SearchInput 
              type="text"
              name="q"
              ref={inputRef}
              value={searchInputValue}
              id="searchspring-search-input"
              onFocus={() => setHasFocus(true)}
              placeholder="What can we help you find?"
              onChange={e => updateSearchInput(e.target.value)}
            />
            {(searchInputValue) && (
              <ClearInputButton type="reset" onClick={e => handleClearClick(e)}>
                <X fontSize={12} />
              </ClearInputButton>
            )}
          </form>
          <SearchButton onClick={handleIconClick}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none"><path fill="#1A1B1B" fillRule="evenodd" d="m12.893 11.609 4.842 4.842a.908.908 0 0 1-1.284 1.283l-4.842-4.842a7.2 7.2 0 1 1 1.283-1.283Zm-5.692.99a5.4 5.4 0 1 0 0-10.8 5.4 5.4 0 0 0 0 10.8Z" clipRule="evenodd"/></svg>
          </SearchButton>
          {(hasFocus) && (
            <CancelButton onClick={() => setHasFocus(false)}>
              Cancel
            </CancelButton>
          )}
        </InputWrap>
      </Container>
      <SearchFlyout isVisible={hasFocus} transformHeight={transformHeight} />
    </div>
  );
}

const SearchWrapper = () => {
  const contentfulConfigurationId = root?.dataset?.contentfulSearchConfigId;
  const searchspringConfigEl = document.getElementById('react-searchspring-config');
  const siteId = searchspringConfigEl?.dataset.siteId;

  // Bail if Searchspring is not configured
  if (!siteId) return;

  return (
    <SearchContextProvider 
      contentfulConfigurationId={contentfulConfigurationId}
      searchspringSiteId={siteId}
    >
      <SearchspringSearch />
    </SearchContextProvider>
  )  
}

createRoot(root).render(<SearchWrapper />);